// The call metadata editor (title + attendees), used by the Documents
// library viewer. Split out when the review surfaces moved to admin
// (ruling 2026-08-20) and the rest of the shared call components died.

import { useState } from 'react';
import { AlertCircle, Loader2 } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import {
  callOrgUsersQueryOptions,
  useUpdateTranscriptMutation,
  type CallTranscript,
} from '@/lib/callActions';

// Cobalt primary action (the app's brand primary; the default Button variant is theme-dark).
function ErrorState({ message }: { message: string }) {
  return (
    <div className="border-l-4 border-[#FCA5A5] bg-[#FEE2E2] text-[#B91C1C] p-3 text-sm flex items-center gap-2">
      <AlertCircle className="h-4 w-4 flex-shrink-0" />
      {message}
    </div>
  );
}

const PRIMARY_BUTTON = 'bg-[#290BD9] hover:bg-[#2309C5] text-white';

export function EditMetaModal({
  open,
  onClose,
  businessId,
  transcript,
}: {
  open: boolean;
  onClose: () => void;
  businessId: number;
  transcript: CallTranscript;
}) {
  const [title, setTitle] = useState(transcript.title ?? '');
  const [attendeeIds, setAttendeeIds] = useState<number[]>(
    (transcript.attendees ?? []).map((a) => a.user_id).filter((id): id is number => typeof id === 'number'),
  );
  const { data: orgUsers } = useQuery(callOrgUsersQueryOptions(businessId));
  const updateMutation = useUpdateTranscriptMutation(businessId, transcript.id);

  const toggleAttendee = (id: number) =>
    setAttendeeIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const handleSave = () => {
    updateMutation.mutate(
      { title: title.trim() || null, attendee_user_ids: attendeeIds },
      { onSuccess: onClose },
    );
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle>Edit call</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="text-sm">Name</Label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="bg-white mt-1"
              placeholder={transcript.source_filename || 'e.g. Discovery call with Acme'}
            />
            <p className="text-xs text-gray-400 mt-1">
              The original file name is kept; leave blank to show it instead.
            </p>
          </div>
          {orgUsers && orgUsers.length > 0 && (
            <div>
              <Label className="text-sm">Attendees</Label>
              <div className="mt-1 space-y-1 max-h-40 overflow-y-auto">
                {orgUsers.map((u) => (
                  <label key={u.user_id} className="flex items-center gap-2 py-1 cursor-pointer">
                    <Checkbox checked={attendeeIds.includes(u.user_id)} onCheckedChange={() => toggleAttendee(u.user_id)} />
                    <span className="text-sm text-gray-700">{u.name || u.email}</span>
                  </label>
                ))}
              </div>
            </div>
          )}
          {updateMutation.isError && <ErrorState message="Could not save changes. Please try again." />}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button className={PRIMARY_BUTTON} onClick={handleSave} disabled={updateMutation.isPending}>
            {updateMutation.isPending ? (
              <>
                <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Saving...
              </>
            ) : (
              'Save changes'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
